import { Download, Paperclip, RotateCcw } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { personsApi } from '../../../human-resources/personnel/personnelApi';
import { maintenanceItemsApi } from '../buildingManagementApi';
import {
  ACTIVITY_TYPE_LABEL,
  MAINTENANCE_ACTIVITY_TYPE,
  type MaintenanceActivityType,
  type MaintenanceItemDetail,
  type MaintenanceRecordListItem,
} from '../buildingManagementTypes';

/** The type FACET's values — the enum names, as the server emits and parses them. */
const TYPE_FACET: Record<MaintenanceActivityType, string> = {
  [MAINTENANCE_ACTIVITY_TYPE.maintenance]: 'Maintenance',
  [MAINTENANCE_ACTIVITY_TYPE.compliance]: 'Compliance',
  [MAINTENANCE_ACTIVITY_TYPE.submission]: 'Submission',
};
import { AttachMaintenanceDocumentModal } from './AttachMaintenanceDocumentModal';

/** A record with the performer's name resolved for the page on screen — never stored. */
type RecordRow = MaintenanceRecordListItem & { performedByName: string | null };

/**
 * An item's ACTIVITIES — every maintenance, compliance and submission recorded against it, its
 * document, and who performed it.
 *
 * **Filtered by type from the cog**, beside Show deleted: nothing ticked is every type.
 *
 * **Paged on the SERVER**, because a weekly item's history grows without bound: search, sort and
 * paging all go through the records endpoint. The performer's NAME is resolved per page, in ONE
 * batch for the ids on screen, through the endpoint that applies the access level.
 */
export function MaintenanceRecordsPanel({
  item,
  onRecord,
  onChanged,
}: {
  item: MaintenanceItemDetail;
  /** Opens the page's Record Activity form. */
  onRecord: () => void;
  /** A record was deleted, restored or given a document — the page refreshes its counts. */
  onChanged: () => void;
}) {
  const toast = useToast();
  const canReadPeople = usePermission('personnel.read');

  const [showDeleted, setShowDeleted] = useState(false);
  const [types, setTypes] = useState<MaintenanceActivityType[]>([]);
  const [attaching, setAttaching] = useState<MaintenanceRecordListItem | null>(null);
  const [confirming, setConfirming] = useState<{ kind: 'delete' | 'restore'; row: RecordRow } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchPage = useCallback(
    async (query: DataTableQuery, signal: AbortSignal) => {
      const page = await maintenanceItemsApi.records(
        item.id,
        {
          ...query,
          status: showDeleted ? undefined : 'active',
          // Nothing ticked is UNMENTIONED (every type), never "ticked to nothing".
          type: types.length > 0 ? types.map((t) => TYPE_FACET[t]).join(',') : undefined,
        },
        signal,
      );

      const personIds = [
        ...new Set(page.items.map((r) => r.performedByPersonId).filter((id): id is string => id != null)),
      ];

      // A reader without personnel.read is told the name is hidden, not that there is none.
      const names =
        canReadPeople && personIds.length > 0 ? await personsApi.resolve(personIds, signal).catch(() => []) : [];
      const byPersonId = new Map(names.map((n) => [n.personId, n.displayName]));

      return {
        items: page.items.map<RecordRow>((r) => ({
          ...r,
          performedByName:
            r.performedByPersonnelId == null
              ? null
              : !canReadPeople
                ? 'Hidden'
                : (r.performedByPersonId != null ? byPersonId.get(r.performedByPersonId) : undefined) ?? null,
        })),
        total: page.total,
      };
    },
    [item.id, showDeleted, types, canReadPeople],
  );

  const state = useDataTable<RecordRow>({ fetchPage, pageSize: 10, defaultSort: 'performedOn:desc' });

  const download = useCallback(
    async (row: RecordRow) => {
      try {
        const { blob, fileName } = await maintenanceItemsApi.download(item.id, row.id);
        const url = URL.createObjectURL(blob);
        const anchor = window.document.createElement('a');

        anchor.href = url;
        anchor.download = fileName ?? `${item.referenceNumber}-${row.performedOn}`;
        window.document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();

        URL.revokeObjectURL(url);
      } catch (caught) {
        toast.failure(caught instanceof ApiError ? caught.message : 'Could not download the document.');
      }
    },
    [item.id, item.referenceNumber, toast],
  );

  const rowActions = useCallback(
    (row: RecordRow): MenuItem[] => [
      {
        key: `download-${row.id}`,
        label: 'Download document',
        icon: <Download className="h-4 w-4" aria-hidden="true" />,
        permission: 'building-management.read',
        disabledReason: row.attachmentRepositoryItemId != null ? null : 'No document is filed against this activity.',
        onClick: () => void download(row),
      },
      {
        key: `attach-${row.id}`,
        label: row.attachmentRepositoryItemId != null ? 'Replace document' : 'File document',
        icon: <Paperclip className="h-4 w-4" aria-hidden="true" />,
        permission: 'building-management.record',
        disabledReason: row.isActive ? null : 'This activity is deleted. Restore it before filing a document.',
        onClick: () => setAttaching(row),
      },
      {
        key: `restore-${row.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'building-management.delete',
        disabledReason: row.isActive ? 'This activity is already active.' : null,
        onClick: () => setConfirming({ kind: 'restore', row }),
      },
    ],
    [download],
  );

  const columns = useMemo(() => COLUMNS, []);

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'delete') await maintenanceItemsApi.removeRecord(item.id, confirming.row.id);
      else await maintenanceItemsApi.restoreRecord(item.id, confirming.row.id);

      toast.success(
        `${confirming.kind === 'delete' ? 'Deleted' : 'Restored'} ${confirming.row.activity}.`,
      );
      setConfirming(null);
      state.reload();
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="flex h-full min-w-0 flex-col">
      <DataTablePanel<RecordRow>
        id="maintenance-records"
        state={state}
        columns={columns}
        rowKey={(row) => row.id}
        searchPlaceholder="Search the activity or remarks"
        fill
        onCreate={onRecord}
        createLabel="Record activity"
        createPermission="building-management.record"
        createDisabledReason={item.isActive ? null : 'This item is deleted. Restore it before recording against it.'}
        settingsItems={[
          ...Object.entries(ACTIVITY_TYPE_LABEL).map(([value, label]) => {
            const typeValue = Number(value) as MaintenanceActivityType;

            return {
              key: `type-${value}`,
              label,
              section: 'Type',
              checked: types.includes(typeValue),
              onClick: () =>
                setTypes((current) =>
                  current.includes(typeValue) ? current.filter((t) => t !== typeValue) : [...current, typeValue],
                ),
            };
          }),
          {
            key: 'show-deleted',
            label: 'Show deleted',
            section: 'Records',
            checked: showDeleted,
            onClick: () => setShowDeleted((v) => !v),
          },
        ]}
        rowActions={rowActions}
        onDelete={(row) => setConfirming({ kind: 'delete', row })}
        deletePermission="building-management.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(row) => (row.isActive ? null : 'This activity is already deleted.')}
        otherInfo={(row) => row}
        emptyMessage="Nothing recorded yet. Record an activity when work is done, a permit is complied with, or a report is submitted."
        emptySearchMessage="No activity matches that search."
      />

      {attaching ? (
        <AttachMaintenanceDocumentModal
          open
          itemId={item.id}
          record={attaching}
          onClose={() => setAttaching(null)}
          onSaved={() => {
            setAttaching(null);
            state.reload();
            onChanged();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'delete' ? 'Delete activity' : 'Restore activity'}
          danger={confirming.kind === 'delete'}
          requireConfirm={confirming.kind === 'delete'}
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`maintenance-record-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              {confirming.kind === 'delete' ? (
                <>
                  <p>
                    {confirming.row.activity} ({formatDateOnly(confirming.row.performedOn)}) leaves this
                    item&apos;s history. Its document stays in the Repository, and the activity can be restored.
                  </p>

                  {/*
                    IT DOES LESS THAN A READER MIGHT EXPECT, and that is said before the tick: a due
                    date this activity moved the item to stays where it is. Correcting the schedule
                    is an Edit on the item, where it is audited as what it is. Said only where the
                    activity DID renew — otherwise there is nothing to move back.
                  */}
                  {confirming.row.nextDueDate != null ? (
                    <p>
                    The item&apos;s due date is <strong>not</strong> moved back. If the schedule is now
                    wrong, edit the item&apos;s due date.
                    </p>
                  ) : null}
                </>
              ) : (
                <p>
                  {confirming.row.activity} ({formatDateOnly(confirming.row.performedOn)}) returns to this item&apos;s history.
                </p>
              )}

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}

const COLUMNS: DataColumn<RecordRow>[] = [
  {
    key: 'performedOn',
    header: 'Performed On',
    width: 140,
    sortable: true,
    render: (row) => <TextCell value={formatDateOnly(row.performedOn)} mono />,
  },
  {
    key: 'typeLabel',
    header: 'Type',
    width: 130,
    summary: true,
    render: (row) => <TextCell value={row.typeLabel} />,
  },
  {
    key: 'activity',
    header: 'Activity',
    width: 280,
    summary: true,
    render: (row) => <TextCell value={row.activity} />,
  },
  {
    key: 'performedByName',
    header: 'Performed By',
    width: 200,
    // Resolved for this page, not stored — so not sortable or searchable.
    render: (row) => <TextCell value={row.performedByName} />,
  },
  {
    key: 'previousDueDate',
    header: 'Previous Due Date',
    width: 130,
    // An em dash where the activity did not renew the schedule.
    render: (row) => <TextCell value={row.previousDueDate != null ? formatDateOnly(row.previousDueDate) : null} mono />,
  },
  {
    key: 'nextDueDate',
    header: 'Next Due',
    width: 130,
    sortable: true,
    render: (row) => <TextCell value={row.nextDueDate != null ? formatDateOnly(row.nextDueDate) : null} mono />,
  },
  {
    key: 'remarks',
    header: 'Remarks',
    width: 300,
    render: (row) => <TextCell value={row.remarks} />,
  },
  {
    key: 'document',
    header: 'Document',
    width: 120,
    render: (row) => <TextCell value={row.attachmentRepositoryItemId != null ? 'Filed' : null} />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (row) =>
      row.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
